import React, { useEffect, useState, useMemo } from "react";
import { useFilters } from "../../../context/FilterContext";
import {
    Box, TextField, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, TablePagination,
    Typography, CircularProgress, Button,
    Autocomplete
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { toast } from "react-toastify";

import {
    getAttendanceByCoe,
    getTimeSlotsByLabType
} from "../../../services/reportServices/adminReportServices/AttendanceReportServices.js";
import { getAllCOE } from "../../../services/adminServices/CoeServices.js";
import { getLabType } from "../../../services/mentorServices/MentorServices.js";
import { getAdminCheckInOutReport } from "../../../services/reportServices/adminReportServices/AttendanceReportServices";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 16,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const CheckInOutReport = () => {
    const { checkInOutReportState, setCheckInOutReportState } = useFilters();

    const [data, setData] = useState(checkInOutReportState.data);
    const [attendanceOptions, setAttendanceOptions] = useState(checkInOutReportState.options.attendanceOptions || []);
    const [timeSlots, setTimeSlots] = useState(checkInOutReportState.options.timeSlots || []);
    const [allCOE, setCOE] = useState(checkInOutReportState.options.allCOE || []);
    const [labTypes, setLabTypes] = useState(checkInOutReportState.options.labTypes || []);
    const [selection, setSelection] = useState(() => {
        if (checkInOutReportState.selection && checkInOutReportState.selection.coe) {
            return checkInOutReportState.selection;
        }
        const user = JSON.parse(localStorage.getItem('user')) || {};
        return {
            coe: user.coeId || user.coe_id || "",
            labType: "",
            attendanceName: "",
            timeSlot: "",
            fromDate: "",
            toDate: ""
        };
    });
    const [page, setPage] = useState(checkInOutReportState.page);
    const [rowsPerPage, setRowsPerPage] = useState(checkInOutReportState.rowsPerPage);
    const [loading, setLoading] = useState(false);
    const [showTable, setShowTable] = useState(checkInOutReportState.showTable);

    useEffect(() => {
        setCheckInOutReportState({
            selection, data, showTable, page, rowsPerPage,
            options: { allCOE, labTypes, attendanceOptions, timeSlots },
            hasFetched: checkInOutReportState.hasFetched
        });
    }, [selection, data, showTable, page, rowsPerPage, 
        allCOE, labTypes, attendanceOptions, timeSlots,
        setCheckInOutReportState, checkInOutReportState.hasFetched]);

    useEffect(() => {
        if (allCOE.length === 0) {
            getAllCOE().then(res => {
                const sortedLabs = res.data.data?.sort((a, b) => a.name?.localeCompare(b.name)) || [];
                setCOE(sortedLabs || [])
            }).catch(err => toast.error("Failed to fetch COEs"));
        }
        if (labTypes.length === 0) {
            getLabType().then(res => setLabTypes(res?.data?.data?.value || [])).catch(() => setLabTypes([]));
        }
    }, []);

    useEffect(() => {
        if (selection.coe && selection.labType) {
            if (attendanceOptions.length === 0) {
                getAttendanceByCoe(selection.coe, selection.labType)
                    .then(res => setAttendanceOptions(res?.data?.data || []))
                    .catch(() => setAttendanceOptions([]));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.coe, selection.labType]);

    useEffect(() => {
        if (selection.coe && selection.labType && selection.attendanceName) {
            if (timeSlots.length === 0) {
                getTimeSlotsByLabType(selection.coe, selection.labType, selection.attendanceName)
                    .then(res => setTimeSlots(res?.data?.data || []))
                    .catch(() => setTimeSlots([]));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.coe, selection.labType, selection.attendanceName]);

    const isValidSelection = useMemo(() => {
        return selection.coe && selection.labType && selection.attendanceName;
    }, [selection]);

    const handleSelectionChange = ({ target: { name, value } }) => {
        const updated = { ...selection, [name]: value };

        // Clear dependent fields
        if (name === "coe") updated.labType = updated.attendanceName = updated.timeSlot = "";
        else if (name === "labType") updated.attendanceName = updated.timeSlot = "";
        else if (name === "attendanceName") updated.timeSlot = "";

        if (["fromDate", "toDate"].includes(name)) {
            const { fromDate, toDate } = { ...updated, [name]: value };
            if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
                toast.error("'To Date' should be after 'From Date'");
                return;
            }
        }

        setSelection(updated);
    };

    const fetchAllData = async () => {
        if (!isValidSelection) return;
        setLoading(true);
        try {
            const res = await getAdminCheckInOutReport(
                selection.coe,
                selection.labType,
                selection.timeSlot,
                selection.attendanceName,
                selection.fromDate,
                selection.toDate,
            );
            setData(res?.data?.data || []);
            setShowTable(true);
            setCheckInOutReportState(prev => ({ ...prev, hasFetched: true }));

        } catch (err) {
            toast.error(err?.response?.data?.message || "Error fetching data");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() =>
        [...data].sort((a, b) => new Date(b.AttendanceDate) - new Date(a.AttendanceDate))
        , [data]);

    const paginatedRows = useMemo(() =>
        sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        , [sortedData, page, rowsPerPage]);

    return (
        <Box>
            <Typography className="dashboard-header-title" pt={2} pb={4} width="100%">
                Welcome {JSON.parse(localStorage.getItem("user"))?.name || "User"}! to the CheckIn and CheckOut Report.
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, minWidth: "100%" }}>
                {[{ label: "COE*", name: "coe", options: allCOE },
                { label: "Lab Type*", name: "labType", options: labTypes }].map(({ label, name, options }) => (
                    <TextField
                        key={name}
                        select
                        label={label}
                        name={name}
                        value={selection[name]}
                        onChange={handleSelectionChange}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: "258px" }}
                    >
                        <MenuItem value="">None</MenuItem>
                        {options.map((opt, i) => (
                            <MenuItem key={i} value={opt.id || opt}>
                                {opt.name || opt}
                            </MenuItem>
                        ))}
                    </TextField>
                ))}

                <Autocomplete
                    options={attendanceOptions?.sort((a, b) =>
                        a.attendanceName?.localeCompare(b.attendanceName)
                    )}
                    getOptionLabel={(option) => option.attendanceName || ""}
                    value={attendanceOptions.find(opt => opt.attendanceName === selection.attendanceName) || null}
                    onChange={(e, newValue) => {
                        handleSelectionChange({
                            target: { name: "attendanceName", value: newValue?.attendanceName || "" }
                        });
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Attendance Name*"
                            variant="outlined"
                            size="small"
                            sx={{ minWidth: "258px" }}
                        />
                    )}
                />

                <TextField
                    select
                    label="Session Time"
                    name="timeSlot"
                    value={selection.timeSlot}
                    onChange={handleSelectionChange}
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: "258px" }}
                >
                    <MenuItem value="">None</MenuItem>
                    {timeSlots.map((opt, i) => (
                        <MenuItem key={i} value={opt.timeSlot}>
                            {opt.timeSlot}
                        </MenuItem>
                    ))}
                </TextField>

                {["From Date", "To Date"].map((label, i) => (
                    <TextField
                        key={label}
                        type="date"
                        name={i === 0 ? "fromDate" : "toDate"}
                        value={selection[i === 0 ? "fromDate" : "toDate"]}
                        label={label}
                        onChange={handleSelectionChange}
                        size="small"
                        sx={{ minWidth: "258px" }}
                        InputLabelProps={{ shrink: true }}
                    />
                ))}

                <Button
                    variant="contained"
                    sx={{ minWidth: "200px" }}
                    onClick={fetchAllData}
                    disabled={!isValidSelection || loading}
                >
                    {loading ? <CircularProgress size={24} /> : "VIEW"}
                </Button>
            </Box>
            {showTable &&
                <TableContainer component={Paper} className="table-container" sx={{ width: "100%", overflowX: "auto", mt: 3 }}>
                    <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
                        <TableHead>
                            <TableRow>
                                {["Date", "Attendance Name", "Session Time", "Total Strength", "#CheckIn Count", "#Not CheckIn Count", "#CheckOut Count", "#Not CheckOut Count", "% Attended"]
                                    .map((header) => <StyledTableCell key={header}>{header}</StyledTableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.length ? paginatedRows.map((row, idx) => (
                                <StyledTableRow key={idx}>
                                    <StyledTableCell>{row?.AttendanceDate || "-"}</StyledTableCell>
                                    <StyledTableCell  sx={{ textTransform: "none !important" }}>{row?.attendanceName || "-"}</StyledTableCell>
                                    <StyledTableCell>{row?.SessionTime || "-"}</StyledTableCell>
                                    <StyledTableCell>{row?.TotalLearners || 0}</StyledTableCell>
                                    <StyledTableCell>{row?.PresentLearners || 0}</StyledTableCell>
                                    <StyledTableCell>{row?.AbsentLearners || 0}</StyledTableCell>
                                    <StyledTableCell>{row?.CheckOutCount || 0}</StyledTableCell>
                                    <StyledTableCell>{row?.NotCheckOutCount || 0}</StyledTableCell>
                                    <StyledTableCell>{Math.round(row?.PercentagePresentLearners) || 0}%</StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <TableRow>
                                    <StyledTableCell colSpan={7} align="center">
                                        No records found.
                                    </StyledTableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[10,20,30,60,100]}
                        count={data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>

            }

        </Box>
    );
};

export default CheckInOutReport;
